// English strings for P7b-basic (slot `marketing`: REVIEW-02) (D-013: English only). Spread into
// `en` as `en.marketing`, so keys read `t("marketing.…")`. Copy rules: DESIGN_SYSTEM §9.6.
export const marketingStrings = {
  reviews: {
    title: "Reviews",
    lead: "New reviews wait here until you publish them. Hidden reviews keep their reason and are never deleted.",
    empty: "No reviews here.",
    caption: "Product reviews",
    states: { pending: "Waiting", published: "Published", hidden: "Hidden" },
    views: { all: "All", pending: "Waiting", published: "Published", hidden: "Hidden" },
    viewsLabel: "Review status",
    rating: "{rating} of 5",
    verified: "Verified purchase",
    // Owner, 2026-11-05: beside a review the shop added or imported.
    manual: "Manual",
    sources: {
      verified: "Verified purchase",
      customer: "Customer",
      merchant: "Added by you",
      imported: "Imported",
    },
    hiddenBecause: "Hidden: {reason}",
    reason: "Reason for hiding",
    reasonPh: "Write the reason",
    publish: "Publish",
    hide: "Hide",
    saved: "Review updated",
    published: "Review published",
    hidden: "Review hidden",
    replySaved: "Reply saved",
    replyRemoved: "Reply removed",
    replied: "You replied",
    close: "Close",
    cancel: "Cancel",
    unknownProduct: "Deleted product",
    starsOnly: "{stars} stars only",
    searchLabel: "Search reviews",
    searchPh: "Customer, product or words",
    showAll: "Show all reviews",
    noMatchTitle: "No reviews match",
    noMatchBody: "Try another status, star rating or search.",
    emptyTitle: "No reviews yet",
    emptyBody:
      "Reviews from your store appear here for you to publish. You can also add one yourself or import a spreadsheet.",
    columns: {
      hash: "#",
      product: "Product",
      rating: "Rating",
      review: "Review",
      author: "Customer",
      status: "Status",
      date: "Date",
      actions: "Actions",
    },
    summary: {
      average: "Average rating",
      outOf: "out of 5",
      published: { one: "From {count} published review", other: "From {count} published reviews" },
      waiting: "Waiting for you",
      review: "Review them",
    },
    hideTitle: "Hide this review",
    hideLead:
      "It's taken off your store but kept here with the reason. You can publish it again later.",
    reasons: {
      spam: "Spam or advertising",
      offensive: "Offensive language",
      offTopic: "Not about this product",
      fake: "Fake or misleading",
      personal: "Shares personal details",
      other: "Other",
    },
    detail: {
      written: "Written {date}",
      reply: "Your reply",
      replyPh: "Thank the customer or answer their question. Your reply shows under the review.",
      replyShown: "Shown under the review on your store.",
      replyLater: "Shown once the review is published.",
      saveReply: "Save reply",
      removeReply: "Remove reply",
      publishWithReply: "Publish with reply",
      openProduct: "Open the product",
    },
    menu: {
      label: "Actions for the review by {name}",
      review: "Review",
      view: "View review",
      reply: "Reply",
      editReply: "Edit reply",
      product: "Open product",
      status: "Status",
    },
    form: {
      title: "Add review",
      product: "Product",
      productPh: "Search your products",
      searching: "Searching…",
      noProducts: "No product matches.",
      change: "Change",
      review: "Review",
      rating: "Rating",
      ratingWords: { r1: "Poor", r2: "Fair", r3: "Good", r4: "Very good", r5: "Excellent" },
      authorPh: "Customer name",
      date: "Date written",
      titlePh: "Title (optional)",
      body: "Review",
      bodyPh: "What the customer said",
      preview: "Preview",
      previewTitle: "Review title",
      previewBody: "The review text shows here.",
      previewAuthor: "Customer name",
      when: "On your store",
      publishNow: "Publish now",
      holdIt: "Keep waiting",
      honest:
        "Add only real reviews your customers gave you, for example on WhatsApp. They're marked “Added by you”, never as a verified purchase.",
      add: "Add review",
      added: "Review added",
      needed: "Pick a product and enter the customer's name and review.",
    },
    import: {
      button: "Import",
      title: "Import reviews",
      file: "Spreadsheet",
      template: "Download template",
      choose: "Choose a CSV file",
      columns: "Columns: product (handle or SKU), rating 1–5, title, body, author, date",
      rows: { one: "{count} row", other: "{count} rows" },
      problems: {
        product: "No product handle or SKU",
        rating: "Rating isn't 1 to 5",
        body: "No review text",
        author: "No author name",
      },
      ready: "Ready to import",
      skipped: "Rows with problems",
      tooMany: "Import up to {max} reviews at a time.",
      note: "Imported reviews are marked “Imported”. Rows already imported are skipped, so you can import the same file again safely.",
      run: { one: "Import {count} review", other: "Import {count} reviews" },
      needed: "Choose a file with at least one complete row.",
      imported: "Imported",
      duplicates: "Already there",
      unmatched: "Product not found",
      unmatchedRows: "Lines in your file: {rows}",
    },
  },
  // Marketing tabs built 2026-10-22 (owner: "check this section … if missing then build"): coupons
  // popup, newsletter subscribers, flash deals, offer pop-ups, checkout links, affiliates.
  promo: {
    summary: "Summary",
    pickConfirm: { one: "Add {count} item", other: "Add {count} items" },
    save: "Save changes",
    edit: "Edit",
    delete: "Delete",
    remove: "Remove",
    pause: "Pause",
    resume: "Resume",
    product: "Product",
    noLimit: "No limit",
    never: "Never",
    menuFor: "Actions for {name}",
    filterLabel: "Show",
    noMatch: "Nothing matches",
    noMatchBody: "Try another search or show all.",
    changed: "Someone changed this while you were editing. Close and open it again.",
    copied: "Copied",
    copyFailed: "Couldn't copy. Select the text and copy it.",
    filters: {
      all: "All",
      running: "Running",
      scheduled: "Scheduled",
      paused: "Paused",
      ended: "Ended",
    },
    state: { running: "Running", scheduled: "Scheduled", paused: "Paused", ended: "Ended" },
    left: { dh: "{d}d {h}h", hm: "{h}h {m}m", m: "{m}m" },
    coupon: {
      edit: "Edit coupon {code}",
      codePh: "Code, e.g. DASHAIN10",
      codeFixed: "The code can't change",
      copy: "Copy code",
      cap: "Largest discount",
      capHelp: "Optional. Stops a big order getting a huge discount.",
      freeHelp: "The customer pays no delivery charge.",
      methods: "Works with",
      anyMethod: "Any payment",
      method: {
        cod: "Cash on delivery",
        esewa: "eSewa",
        khalti: "Khalti",
        fonepay_qr: "Fonepay QR",
        fonepay_web: "Fonepay",
        manual_qr: "Bank QR",
      },
      starts: "Starts",
      startsHelp: "Empty: works from now.",
      ends: "Ends",
      endsHelp: "Empty: never ends.",
      auto: "Apply by itself",
      autoHint: "No code needed at checkout",
      stacking: "Use with other coupons",
      stackingHint: "Can add to another coupon",
      active: "On",
      activeHint: "Off pauses the coupon",
      none: "None",
      valid: "Valid",
      hint: "Customers type the code at checkout, in any letter case.",
      autoSide: "Checkout adds this discount by itself when the order qualifies.",
      sec: { rules: "Rules", when: "When", options: "Options" },
      why: { value: "Enter the discount." },
      tiles: {
        live: "Live coupons",
        liveHint: "Customers can use them now",
        used: "Times used",
        usedHint: "On all coupons",
        ending: "Ending this week",
        endingHint: "None end in the next 7 days",
        scheduled: "Scheduled",
        scheduledHint: "Start later",
      },
    },
    news: {
      lead: "People who agreed to get your offers by email or SMS. Email sign-ups count once the person clicks the link in the confirmation email. You can't add people here: they have to agree themselves.",
      export: "Export CSV",
      exportEmpty: "Nothing to export in this view",
      exported: { one: "{count} subscriber exported", other: "{count} subscribers exported" },
      search: "Search email, phone or name",
      filters: {
        all: "All",
        subscribed: "Subscribed",
        pending: "Waiting",
        unsubscribed: "Unsubscribed",
      },
      status: { subscribed: "Subscribed", pending: "Waiting", unsubscribed: "Unsubscribed" },
      source: {
        newsletter: "Newsletter form",
        checkout: "Checkout",
        admin: "Added by staff",
        stock_alert: "Back-in-stock alert",
      },
      channel: { email: "Email", sms: "SMS" },
      cols: {
        contact: "Subscriber",
        source: "Signed up from",
        signedUp: "Signed up",
        status: "Status",
        changed: "Last change",
      },
      confirmedOn: "Confirmed {date}",
      leftOn: "Left {date}",
      notConfirmed: "Hasn't clicked the email link yet",
      copyEmail: "Copy email",
      copyPhone: "Copy phone number",
      unsubscribe: "Unsubscribe",
      unsubscribed: "{contact} unsubscribed",
      unsubscribeHint:
        "They stop getting marketing messages and go on the do-not-message list. Order updates still reach them. Only they can subscribe again.",
      reason: "Reason",
      reasons: {
        customer_request: "They asked to stop",
        complaint: "Complaint",
        bounce: "Messages bounce",
        other: "Other",
      },
      emptyTitle: "No subscribers yet",
      emptyBody:
        "Sign-ups from the newsletter form and the checkout box appear here with the date and how they agreed.",
      tiles: {
        subscribed: "Subscribed",
        subscribedHint: "Get your offers",
        pending: "Waiting",
        pendingHint: "Haven't confirmed their email",
        unsubscribed: "Unsubscribed",
        unsubscribedHint: "Stopped your messages",
        new: "New this month",
        newHint: "In the last 30 days",
      },
      csv: {
        contact: "Email or phone",
        channel: "Channel",
        name: "Customer",
        status: "Status",
        source: "Signed up from",
        signedUp: "Signed up",
        confirmed: "Confirmed",
        unsubscribed: "Unsubscribed",
      },
    },
    deals: {
      lead: "Lower the price of chosen products for a set time. Prices go back by themselves when the deal ends. Showing deal prices on the online store is coming soon.",
      new: "New flash deal",
      edit: "Edit flash deal",
      create: "Create deal",
      created: "Flash deal {name} created",
      saved: "Flash deal {name} saved",
      deleted: "Flash deal deleted",
      paused: "{name} paused",
      resumed: "{name} is on again",
      deleteTitle: "Delete {name}?",
      deleteBody: "Prices go back to normal straight away. Orders already placed keep their price.",
      search: "Search deals or products",
      offerPercent: "{value}% off",
      offerFixed: "{value} off",
      startsIn: "Starts in {left}",
      emptyTitle: "No flash deals yet",
      emptyBody: "Pick products, set the discount and the start and end time.",
      cols: {
        deal: "Deal",
        discount: "Discount",
        starts: "Starts",
        ends: "Ends",
        left: "Time left",
        state: "State",
      },
      tiles: {
        running: "Running now",
        runningHint: "Deals customers see now",
        endsNext: "Next to end",
        startsNext: "Next to start",
        none: "Nothing planned",
        products: "Products on sale",
        productsHint: "In running deals",
      },
      sec: {
        deal: "Deal",
        discount: "Discount",
        products: "Products",
        when: "When",
        options: "Options",
      },
      namePh: "Deal name, e.g. Weekend pashmina sale",
      percent: "Percent off",
      fixed: "Amount off",
      percentOff: "Percent off",
      amountOff: "Amount off each",
      addProducts: "Add products",
      inOtherDeal:
        "A product here is already in another deal at the same time. Remove it or change the dates.",
      starts: "Starts",
      ends: "Ends",
      runFor: "Run for",
      quick: { day: "24 hours", three: "3 days", week: "1 week" },
      length: "Length",
      countdown: "Show countdown",
      countdownHint: "A timer on the product page",
      active: "On",
      activeHint: "Off pauses the deal",
      limit: "Most per order",
      limitHelp: "How many of each product one order gets at the deal price.",
      hint: "Each product can be in one deal at a time.",
      why: {
        name: "Name the deal.",
        value: "Enter the discount.",
        products: "Add at least one product.",
        window: "The end must be after the start.",
      },
    },
    pop: {
      lead: "Show a discount, an announcement or a newsletter sign-up when customers open your store. Showing pop-ups on the online store and counting views are coming soon.",
      new: "New pop-up",
      edit: "Edit pop-up",
      create: "Create pop-up",
      created: "Pop-up {name} created",
      saved: "Pop-up {name} saved",
      deleted: "Pop-up deleted",
      paused: "{name} paused",
      resumed: "{name} is on again",
      deleteTitle: "Delete {name}?",
      deleteBody: "Customers stop seeing it.",
      emptyTitle: "No pop-ups yet",
      emptyBody: "Welcome new visitors with a coupon or tell them about a sale.",
      cols: {
        popup: "Pop-up",
        kind: "Type",
        shows: "Shows",
        where: "Where",
        until: "Until",
        state: "State",
      },
      kind: { discount: "Discount", announcement: "Announcement", newsletter: "Newsletter" },
      trigger: {
        on_load: "Right away",
        after_delay: "After a wait",
        on_scroll: "On scroll",
        exit_intent: "When leaving",
      },
      afterSeconds: "After {s} seconds",
      pages: { all: "Every page", home: "Home page", products: "Product pages", cart: "Cart" },
      devices: { all: "All devices", mobile: "Phones", desktop: "Computers" },
      frequency: { once: "Once per visitor", daily: "Once a day", every_visit: "Every visit" },
      tiles: {
        showing: "Showing now",
        none: "No pop-up is on",
        views: "Views",
        clicks: "Clicks",
        counting: "Counted once pop-ups show on the store",
        rate: "Click rate",
        rateHint: "Clicks for each view",
      },
      sec: {
        kind: "Type",
        content: "Content",
        when: "When it shows",
        where: "Where and how often",
        schedule: "Schedule",
      },
      namePh: "Name (only you see it)",
      titlePh: "Heading",
      bodyPh: "Message",
      buttonPh: "Button text",
      urlPh: "Button link, e.g. /collections/all",
      coupon: "Coupon to show",
      pickCoupon: "Pick a coupon",
      wait: "Wait",
      seconds: "{s} s",
      pagesLabel: "Pages",
      devicesLabel: "Devices",
      frequencyLabel: "How often",
      starts: "Starts",
      startsHelp: "Empty: from now.",
      ends: "Ends",
      endsHelp: "Empty: until you pause it.",
      active: "On",
      activeHint: "Off hides the pop-up",
      preview: "Preview",
      emailPh: "Your email",
      hint: "If more than one pop-up is on, the store shows the newest.",
      why: {
        title: "Add a heading.",
        coupon: "Pick the coupon to show.",
        button: "Add the button text.",
      },
      defaults: {
        discountTitle: "10% off your first order",
        discountBody: "Use this code at checkout.",
        discountButton: "Shop now",
        announceTitle: "Free delivery inside the valley",
        announceBody: "On orders above Rs 2,000 this week.",
        announceButton: "See the sale",
        newsTitle: "Get our offers first",
        newsBody: "New arrivals and sales, about once a week.",
        newsButton: "Subscribe",
      },
    },
    links: {
      lead: "A link that opens checkout with the products, quantities and coupon already in the cart. Share it on Instagram, TikTok or in a chat.",
      new: "New checkout link",
      edit: "Edit checkout link",
      create: "Create link",
      created: "Link {name} created and copied",
      saved: "Link {name} saved",
      deleted: "Link deleted",
      paused: "{name} paused",
      resumed: "{name} works again",
      deleteTitle: "Delete {name}?",
      deleteBody: "The link stops working. Orders already placed from it are kept.",
      search: "Search links",
      copy: "Copy link",
      open: "Open on store",
      opensOf: "{used} of {max}",
      cardLine: "{opens} opens · {orders} orders",
      emptyTitle: "No checkout links yet",
      emptyBody: "Choose products and quantities, add a coupon if you like, and copy the link.",
      filters: { all: "All", active: "Working", paused: "Paused", expired: "Expired" },
      state: { active: "Working", paused: "Paused", expired: "Expired" },
      cols: {
        link: "Link",
        items: "Items",
        coupon: "Coupon",
        opens: "Opens",
        orders: "Orders",
        sales: "Sales",
        state: "State",
      },
      tiles: {
        active: "Working links",
        activeHint: "Open checkout now",
        opens: "Opens",
        opensHint: "Times people opened a link",
        orders: "Orders",
        ordersHint: "Placed from a link",
        rate: "{rate}% of opens ordered",
        sales: "Sales",
        salesHint: "From orders placed from a link",
      },
      sec: {
        link: "Link",
        items: "Products in the cart",
        discount: "Discount",
        options: "Options",
      },
      namePh: "Name, e.g. Instagram: Dashain gift set",
      itemsFixed: "The products of a saved link don't change. Make a new link for other products.",
      more: "One more",
      less: "One less",
      coupon: "Coupon",
      noCoupon: "No coupon",
      code: "Link ending",
      codeHelp: "4–32 letters, numbers, - or _. Empty: made for you.",
      codeInvalid: "Use 4–32 letters, numbers, - or _.",
      codeTaken: "Another link already ends like this.",
      autoCode: "AUTO",
      expires: "Expires",
      expiresHelp: "Empty: never expires.",
      maxUses: "Most opens",
      maxUsesHelp: "The link stops after this many opens.",
      active: "Working",
      activeHint: "Off pauses the link",
      pieces: "Items",
      subtotal: "Subtotal",
      preview: "Link",
      hint: "The link is copied when you create it.",
      editHint: "People who open a paused or expired link see a message instead of checkout.",
      createdOn: "Made {date}",
      why: { name: "Name the link.", items: "Add at least one product." },
    },
    aff: {
      lead: "Partners who share your store with their code earn a commission on the orders it brings. Counting orders from their links is coming soon; you can add partners and record payments now.",
      new: "Add affiliate",
      edit: "Edit affiliate",
      create: "Add affiliate",
      created: "{name} added",
      saved: "{name} saved",
      deleted: "Affiliate deleted",
      paused: "{name} paused",
      resumed: "{name} is active again",
      deleteTitle: "Delete {name}?",
      deleteBody: "Their link stops earning. Payments already recorded are kept.",
      search: "Search name, code or contact",
      details: "Details",
      copy: "Copy their link",
      active: "Active",
      pausedState: "Paused",
      allProducts: "All products",
      someProducts: "Chosen products",
      perSale: "{value}% of each sale",
      perOrder: "{value} per order",
      emptyTitle: "No affiliates yet",
      emptyBody:
        "Add a partner, choose which products they can promote and set the commission as a percent or a fixed amount.",
      filters: { all: "All", active: "Active", paused: "Paused", owed: "To pay" },
      cols: {
        partner: "Partner",
        code: "Code",
        products: "Products",
        commission: "Commission",
        orders: "Orders",
        sales: "Sales",
        earned: "Earned",
        owed: "To pay",
        state: "State",
      },
      tiles: {
        orders: "Orders referred",
        ordersHint: "From affiliate links",
        sales: "Sales referred",
        salesHint: "Value of those orders",
        earned: "Commission earned",
        earnedHint: "On all affiliates",
        owed: "Commission to pay",
        owedHint: "Earned and not paid yet",
      },
      sec: {
        partner: "Partner",
        link: "Their link",
        commission: "Commission",
        products: "Products",
        more: "More",
      },
      namePh: "Name, e.g. Anjali Travels Vlog",
      emailPh: "Email",
      phonePh: "Phone",
      code: "Code",
      codeHelp: "3–24 capital letters, numbers, - or _. It goes at the end of their link.",
      codeInvalid: "Use 3–24 capital letters, numbers, - or _.",
      codeTaken: "Another affiliate already uses this code.",
      remember: "Counts orders for",
      days: { one: "{count} day", other: "{count} days" },
      percent: "Percent of sale",
      fixed: "Amount per order",
      example: "On a {amount} order",
      theirLink: "Their link",
      notePh: "Note (only you see it)",
      activeToggle: "Active",
      activeHint: "Off stops their link earning",
      hint: "A customer who opens their link and orders within the days above earns them the commission.",
      contact: "Contact",
      since: "Added",
      payouts: "Payments",
      noPayouts: "No payments recorded yet.",
      pay: "Record payment",
      payTitle: "Pay {name}",
      payButton: "Record payment",
      payAll: "Pay all: {amount}",
      paid: "{amount} paid to {name}",
      paying: "Paying now",
      left: "Still to pay",
      amount: "Amount",
      method: "Paid by",
      methods: { cash: "Cash", bank: "Bank", esewa: "eSewa", khalti: "Khalti", other: "Other" },
      referencePh: "Reference (optional)",
      payNotePh: "Note (optional)",
      payHint: "This records a payment you already made. It doesn't send money.",
      tooMuch: "More than what is owed.",
      nothingOwed: "Nothing to pay",
      why: {
        name: "Add their name.",
        value: "Enter the commission.",
        products: "Add at least one product.",
      },
    },
  },
} as const;
